import React, { useState, useEffect, useContext } from 'react';
import NumberGrid from './NumberGrid';
import OperationButtons from './OperationButtons';
import TargetNumber from './TargetNumber';
import HardModeToggle from './HardModeToggle';
import Container from '@mui/material/Container';
import { Grid, Box } from '@mui/material';
import NavBar from './NavBar';
import { DatabaseContext } from '../firebase/db';
import { AuthContext } from '../firebase/auth';
import TopScore from './TopScore';
import UserMessage from './UserMessage';

function Game() {
  const {dailyNumbers, currentScores, topScores, incrementScoreForUser} = useContext(DatabaseContext);
  const {loggedInUser} = useContext(AuthContext);
  
  const [updating, setUpdating] = useState(false);
  const [hardMode, setHardMode] = useState(false);
  const [numbers, setNumbers] = useState(dailyNumbers.numberArray);
  const [target, setTarget] = useState( hardMode ? currentScores.current_score_hard + 1 : currentScores.current_score + 1);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [operationsHistory, setOperationsHistory] = useState([]);
  const [isExploding, setIsExploding] = useState(false);


  useEffect(() => {
    setTarget(hardMode ? currentScores.current_score_hard + 1 : currentScores.current_score + 1);
    setNumbers(dailyNumbers.numberArray);
    setOperationsHistory([]);
  }, [loggedInUser, hardMode, dailyNumbers, currentScores]);

  function handleNumberClick(index) {
    if (selectedIndices.includes(index)) {
        // reset operation 
        setSelectedIndices(selectedIndices.filter((i) => i !== index));
        setSelectedOperation(null);
    } else if (selectedIndices.length > 0 && !selectedOperation) {
        // replace with new selection
        setSelectedIndices([index]);
    } else {
        // append to selection
        setSelectedIndices([...selectedIndices, index]);
    }
  }
  
  function handleOperationClick(operation) {
    if (operation === 'undo') {
        undoOperation();
    } else if (selectedIndices.length !== 1){
        return;
    } else if (operation === selectedOperation) {
      setSelectedOperation(null);
    } else {
      setSelectedOperation(operation);
    }
  }

  function undoOperation() {
    if (operationsHistory.length === 0) return;

    const previousNumbers = operationsHistory.pop();
    setOperationsHistory([...operationsHistory]);
    setNumbers(previousNumbers);
  }

  useEffect(() => {
    if (numbers.length == 0) {
        setNumbers(dailyNumbers.numberArray);
    }
  }, [dailyNumbers])

  useEffect(() => {
    const runUpdate = async () => {
        if (selectedIndices.length === 2 && selectedOperation) {
            const newNumbers = [...numbers];
            const num1 = newNumbers[selectedIndices[0]];
            const num2 = newNumbers[selectedIndices[1]];
            let result;

            switch (selectedOperation) {
                case '+':
                    result = num1 + num2;
                    break;
                case '-':
                    result = num1 - num2;
                    break;
                case '*':
                    result = num1 * num2;
                    break;
                case '/':
                    if (num2 === 0) {
                        return; // Prevent division by zero
                    }
                    result = num1 / num2;
                    if (!Number.isInteger(result)) {
                        setSelectedOperation(null);
                        return; // Only allow integer results for division
                    }
                    result = num1 / num2;
                    break;
                default:
                    return;
            }

            newNumbers[selectedIndices[1]] = result;
            newNumbers[selectedIndices[0]] = null;
            //newNumbers.splice(selectedIndices[0], 1);
            let shouldUpdate = true;

            if (result === target) {
                if (!(hardMode && newNumbers.filter((n) => n !== null).length > 1)) {
                    setUpdating(true);
                    shouldUpdate = await incrementScoreForUser(hardMode);
                    setIsExploding(
                      hardMode ? topScores.maxScoreHard <= currentScores.current_score_hard :
                      topScores.maxScore <= currentScores.current_score
                    )
                    setUpdating(false);
                }
            }
            if (shouldUpdate) {
              setOperationsHistory([...operationsHistory, numbers]);
              setNumbers(newNumbers);
              setSelectedIndices([]);
              setSelectedOperation(null);
            }

        }
    };
    runUpdate();
  }, [selectedIndices, selectedOperation, hardMode, numbers, operationsHistory, target]);

  return (
    <Box>
      <NavBar/>
      <Container>
        <Grid container maxWidth={300} sx={{margin: "auto"}}> 
          <Grid item xs={8}>
            <TargetNumber target={target} isExploding={isExploding} onExplosionComplete={()=>setIsExploding(false)} />
            <TopScore hardMode={hardMode} />
          </Grid>
          <Grid item xs={4} sx={{margin: "auto"}}>
            <HardModeToggle hardMode={hardMode} setHardMode={setHardMode} />
          </Grid>
        </Grid>
        <Grid container maxWidth={300} spacing={2} sx={{margin: "auto"}}>
          <Grid item xs={12} sx={{margin: 'auto'}}>
            <NumberGrid
              numbers={numbers}
              handleNumberClick={handleNumberClick}
              selectedIndices={selectedIndices}
              inactive={updating}
            />
          </Grid>
          <Grid item xs={12} sx={{margin: 'auto'}}>
            <OperationButtons
                handleOperationClick={handleOperationClick}
                selectedOperation={selectedOperation}
                inactive={updating}
            />
          </Grid>
        </Grid>
        <UserMessage 
              numbers={dailyNumbers.numberArray}
              target={target}
              hardMode={hardMode}
              onGameComplete={() => {setIsExploding(true)}}
            />
      </Container>
    </Box>
  );
}

export default Game;
